import { createClient } from '@supabase/supabase-js';
import { isSSR } from '../isSSR.js';
import { debugLog, debugError, debugWarn } from '../../utils/debugLogger.js';
import { SUPABASE_CONFIG } from '../../config/supabaseConfig.js';

// Supabase URL and anon key - from centralized config
const supabaseUrl = SUPABASE_CONFIG.URL;
const supabaseAnonKey = SUPABASE_CONFIG.ANON_KEY;

// Create custom storage mechanism for SSR compatibility
const customStorage = {
  getItem: (key) => {
    try {
      if (isSSR()) {
        debugLog('api', 'Skipping localStorage.getItem in SSR mode', undefined, 'customRoleClient');
        return null;
      }
      return localStorage.getItem(key);
    } catch (e) {
      debugWarn('api', 'Storage getItem error', e, 'customRoleClient');
      return null;
    }
  },
  setItem: (key, value) => {
    try {
      if (isSSR()) {
        debugLog('api', 'Skipping localStorage.setItem in SSR mode', undefined, 'customRoleClient');
        return;
      }
      localStorage.setItem(key, value);
    } catch (e) {
      debugWarn('api', 'Storage setItem error', e, 'customRoleClient');
    }
  },
  removeItem: (key) => {
    try {
      if (isSSR()) {
        debugLog('api', 'Skipping localStorage.removeItem in SSR mode', undefined, 'customRoleClient');
        return;
      }
      localStorage.removeItem(key);
    } catch (e) {
      debugWarn('api', 'Storage removeItem error', e, 'customRoleClient');
    }
  },
};

// Create a Supabase client with the custom role
export const supabaseCustomRole = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: false,
    autoRefreshToken: false,
    storageKey: 'gildy_auth_role_token',
    storage: customStorage,
  },
  global: {
    headers: {
      'x-gildy-client': 'web-app',
      'apikey': supabaseAnonKey,
      'Content-Type': 'application/json'
    },
    fetch: (url, fetchOptions) => {
      // Fix for the "public.public." issue - remove the double prefix
      let modifiedUrl = url;
      if (url.includes('/rest/v1/public.')) {
        modifiedUrl = url.replace('/rest/v1/public.', '/rest/v1/');
        debugLog('api', 'Removing public. prefix from URL', { from: url, to: modifiedUrl }, 'customRoleClient');
      }

      // Add custom headers for role client
      const headers = new Headers(fetchOptions.headers);
      headers.set('x-gildy-client', 'web-app');
      headers.set('apikey', supabaseAnonKey);
      headers.set('Content-Type', 'application/json');
      
      // Add schema header - THIS IS THE KEY FIX
      headers.set('Accept-Profile', 'public');
      headers.set('Content-Profile', 'public');
      
      const newOptions = {
        ...fetchOptions,
        headers
      };

      // Track which tables we're accessing for debug purposes
      const tableName = extractTableNameFromUrl(modifiedUrl);
      if (tableName) {
        debugLog('api', `Accessing table: ${tableName}`, undefined, 'customRoleClient');
      }

      return fetch(modifiedUrl, newOptions)
        .then(response => {
          if (!response.ok) {
            const status = response.status;
            const statusText = response.statusText;
            
            if (status === 404 && tableName) {
              debugError('api', `Table not found or not accessible: ${tableName} (${status} ${statusText})`, undefined, 'customRoleClient');
              
              // If we're trying to access the favorites table, provide a more helpful error message
              if (tableName === 'favorites') {
                debugWarn('api', 'Favorites table issue detected. Please run the fix-favorites-permissions script.', undefined, 'customRoleClient');
              }
            } else if (status === 406 && tableName) {
              debugError('api', `Schema issue with table: ${tableName} (${status} ${statusText})`, undefined, 'customRoleClient');
              
              if (tableName === 'favorites') {
                debugWarn('api', 'Favorites table schema issue detected. Please run the fix-favorites-permissions script.', undefined, 'customRoleClient');
              }
            } else if (status === 401 && tableName) {
              debugError('api', `Authentication issue with table: ${tableName} (${status} ${statusText})`, undefined, 'customRoleClient');
              
              if (tableName === 'favorites') {
                debugWarn('api', 'RLS policy issue detected for favorites table. Please check your authentication status and/or run the fix-favorites-permissions script.', undefined, 'customRoleClient');
              }
            } else {
              debugError('api', `Resource not found: ${modifiedUrl} (${status} ${statusText})`, undefined, 'customRoleClient');
            }
          }
          return response;
        })
        .catch(error => {
          debugError('api', 'Fetch error', { error, url: modifiedUrl }, 'customRoleClient');
          return new Response(JSON.stringify({
            error: 'Network error',
            message: 'Failed to connect to Supabase API',
            code: 'NETWORK_ERROR'
          }), { status: 503 });
        });
    }
  },
  db: {
    schema: 'public' // Explicitly set the schema to public
  }
});

// Helper function to extract table name from a URL
function extractTableNameFromUrl(url) {
  try {
    // Extract table name from URL patterns like /rest/v1/tablename or /rest/v1/tablename?query
    const match = url.match(/\/rest\/v1\/([^?]+)/);
    if (match && match[1]) {
      return match[1];
    }
    return null;
  } catch (e) {
    debugWarn('api', 'Error extracting table name', e, 'customRoleClient');
    return null;
  }
}

// Function to check if a table exists
export const checkTableExists = async (tableName) => {
  if (!tableName) return false;
  
  try {
    const { error } = await supabaseCustomRole
      .from(tableName)
      .select('count')
      .limit(0);
    
    if (!error) return true;
    
    if (error.code === '42P01' || error.message.includes('does not exist')) {
      debugLog('api', `Table ${tableName} doesn't exist`, undefined, 'customRoleClient');
      return false;
    }
    
    debugError('api', `Error checking table ${tableName}`, error, 'customRoleClient');
    return false;
  } catch (err) {
    debugError('api', `Exception checking table ${tableName}`, err, 'customRoleClient');
    return false;
  }
};

// Export helper functions
export const checkCustomRoleClient = () => {
  debugLog('api', 'Client status check', undefined, 'customRoleClient');
  return true;
};
